import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import API_BASE, { setCsrfToken, withApiCredentials } from "../../api";
import {
  getDeletedAccountAuthMessage,
  isDeletedAccountAuthError,
} from "../../auth/deletedAccountMessage";
import { getLocalizedRequiredConsentTexts } from "../../content/legal/localizedConsentTexts";
import countries from "../../data/countries";
import {
  validateCity,
  validateCountry,
  validatePassword,
  validateRequiredConsents,
  validateStep3,
  validateUsername,
} from "./RegisterValidation";

const FISHING_METHODS = [
  "methods.boat_spinning",
  "methods.bottom_fishing",
  "methods.dead_bait",
  "methods.drift_fishing",
  "methods.feeder",
  "methods.fly_fishing",
  "methods.float_fishing",
  "methods.jigging",
  "methods.kayak_fishing",
  "methods.live_bait",
  "methods.other",
  "methods.popping",
  "methods.spinning",
  "methods.surf_casting",
  "methods.trolling",
];

const SPECIES_OPTIONS = [
  "species.albacore_tuna",
  "species.arapaima",
  "species.atlantic_bluefin_tuna",
  "species.atlantic_bonito",
  "species.atlantic_mackerel",
  "species.atlantic_salmon",
  "species.atlantic_tarpon",
  "species.barracuda_european",
  "species.barramundi",
  "species.blacktip_shark",
  "species.bluefish",
  "species.bonefish",
  "species.brown_trout",
  "species.channel_catfish",
  "species.common_bream",
  "species.common_carp",
  "species.common_roach",
  "species.dusky_grouper",
  "species.european_chub",
  "species.european_perch",
  "species.european_sea_bass",
  "species.grass_carp",
  "species.greater_amberjack",
  "species.largemouth_bass",
  "species.mahi_mahi",
  "species.mirror_carp",
  "species.northern_pike",
  "species.peacock_bass",
  "species.pickhandle_barracuda",
  "species.queenfish",
  "species.rainbow_trout",
  "species.red_snapper",
  "species.skipjack_tuna",
  "species.smallmouth_bass",
  "species.tench",
  "species.trevally",
  "species.wahoo",
  "species.wels_catfish",
  "species.yellowfin_tuna",
  "species.zander",
];

const BUSINESS_TYPES = [
  "business_types.gear_brand",
  "business_types.tackle_physical",
  "business_types.tackle_online",
  "business_types.guide",
  "business_types.charter",
  "business_types.boat_rental",
  "business_types.accommodation",
  "business_types.school",
  "business_types.tournament",
  "business_types.private_lake",
  "business_types.day_ticket",
  "business_types.angling_club",
  "business_types.syndicate",
  "business_types.river_lease",
  "business_types.managed_fishery",
  "business_types.bait_supplier",
  "business_types.marina",
  "business_types.boat_service",
  "business_types.outdoor_company",
  "business_types.travel_agency",
  "business_types.ecotourism",
  "business_types.influencer",
  "business_types.youtuber",
  "business_types.content_creator",
  "business_types.photographer",
  "business_types.other",
];

const LANGUAGE_LABELS = {
  en: "English",
  bg: "Bulgarian",
  el: "Greek",
  es: "Spanish",
  fr: "French",
  pt: "Portuguese",
  pl: "Polski",
  de: "Deutsch",
  tr: "T\u00fcrk\u00e7e",
};

function getAvailableLanguages(i18n) {
  const resourceCodes = Object.keys(i18n.options?.resources || {});
  const codes = resourceCodes.length ? resourceCodes : Object.keys(LANGUAGE_LABELS);

  return codes
    .filter((code) => LANGUAGE_LABELS[code])
    .map((code) => ({
      code,
      label: LANGUAGE_LABELS[code],
    }));
}

function getRegisterErrorMessage(errorPayload) {
  const candidates = [
    errorPayload?.message,
    errorPayload?.detail,
    errorPayload?.detail?.message,
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const message = candidate.trim();
    if (message && !message.includes("_")) {
      return message;
    }
  }

  return "";
}

export default function useRegisterFormState({ setUser }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const consentTexts = getLocalizedRequiredConsentTexts(t);
  const availableLanguages = getAvailableLanguages(i18n);

  const [step, setStep] = useState(1);
  const [verificationRequiredState, setVerificationRequiredState] = useState(
    null
  );
  const [isStep1Checking, setIsStep1Checking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const registerRequestInFlightRef = useRef(false);

  const detectedLanguage = i18n.language?.split("-")[0] || "en";
  const [language, setLanguage] = useState(
    availableLanguages.some((lang) => lang.code === detectedLanguage)
      ? detectedLanguage
      : "en"
  );

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [bio, setBio] = useState("");
  const [acceptTermsPrivacy, setAcceptTermsPrivacy] = useState(false);
  const [acceptProfileVisibility, setAcceptProfileVisibility] = useState(false);

  const [country, setCountry] = useState("");
  const [countrySuggestions, setCountrySuggestions] = useState([]);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [city, setCity] = useState("");
  const [selectedCityCoordinates, setSelectedCityCoordinates] = useState(null);
  const [accountType, setAccountType] = useState("");

  const [experience, setExperience] = useState("");
  const [fishingStyle, setFishingStyle] = useState("");
  const [waterType, setWaterType] = useState("");
  const [rangeType, setRangeType] = useState("");
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState([]);

  const [selectedBusinessTypes, setSelectedBusinessTypes] = useState([]);
  const [showBusinessTypeDropdown, setShowBusinessTypeDropdown] =
    useState(false);
  const [otherBusinessDescription, setOtherBusinessDescription] = useState("");
  const [businessArea, setBusinessArea] = useState("");
  const [businessWaterType, setBusinessWaterType] = useState("");
  const [selectedTargetSpecies, setSelectedTargetSpecies] = useState([]);

  const [instagram, setInstagram] = useState("");
  const [youtube, setYoutube] = useState("");
  const [facebook, setFacebook] = useState("");
  const [website, setWebsite] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const [showMethodsDropdown, setShowMethodsDropdown] = useState(false);
  const [showSpeciesDropdown, setShowSpeciesDropdown] = useState(false);
  const [showTargetSpeciesDropdown, setShowTargetSpeciesDropdown] =
    useState(false);
  const [openHelpField, setOpenHelpField] = useState("");

  useEffect(() => {
    if (!openHelpField) return undefined;

    const handlePointerDown = (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest("[data-register-help-root='true']")) {
        setOpenHelpField("");
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenHelpField("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openHelpField]);

  const handleStep1 = async () => {
    if (isStep1Checking) return;

    if (!username || !email || !password) {
      alert(t("register_error_required"));
      return;
    }

    const usernameError = validateUsername(username);
    if (usernameError) {
      alert(t(usernameError));
      return;
    }

    if (username.trim() === password) {
      alert(
        t("username_matches_password", {
          defaultValue: "Your public username must not be the same as your password.",
        })
      );
      return;
    }

    if (!validatePassword(password)) {
      alert(t("password_requirements"));
      return;
    }

    setIsStep1Checking(true);
    try {
      const res = await fetch(
        `${API_BASE}/auth/check?username=${encodeURIComponent(
          username
        )}&email=${encodeURIComponent(email)}`,
        { method: "GET" }
      );

      if (!res.ok) {
        const error = await res.json();

        if (error.detail === "username_already_taken") {
          alert(t("username_already_taken"));
        } else if (error.detail === "email_already_registered") {
          alert(t("email_already_registered"));
        } else {
          alert(t("register_failed"));
        }

        setIsStep1Checking(false);
        return;
      }

      setIsStep1Checking(false);
      setStep(2);
    } catch (err) {
      setIsStep1Checking(false);
      console.error("Check availability error:", err);
      alert(t("server_error"));
    }
  };

  const handleStep2 = () => {
    const step2Error = validateStep3({
      country,
      city,
      accountType,
      t,
    });
    if (step2Error) {
      alert(step2Error);
      return;
    }

    const consentError = validateRequiredConsents({
      acceptTermsPrivacy,
      acceptProfileVisibility,
      consentTexts,
    });
    if (consentError) {
      alert(consentError);
      return;
    }

    if (accountType === "personal") {
      handleRegister();
      return;
    }

    setStep(3);
  };

  const handleCountryChange = (value) => {
    setCountry(value);
    setSelectedCityCoordinates(null);

    if (!value) {
      setCountrySuggestions([]);
      return;
    }

    const filtered = countries.filter((item) =>
      item.toLowerCase().startsWith(value.toLowerCase())
    );

    setCountrySuggestions(filtered.slice(0, 6));
  };

  const selectCountrySuggestion = (value) => {
    setCountry(value);
    setCountrySuggestions([]);
    setSelectedCityCoordinates(null);
  };

  const handleCityChange = async (value) => {
    setCity(value);
    setSelectedCityCoordinates(null);

    if (!value || value.length < 2) {
      setCitySuggestions([]);
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/geo/cities?q=${encodeURIComponent(
          value
        )}&country=${encodeURIComponent(country)}`
      );
      const data = await res.json();
      setCitySuggestions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setCitySuggestions([]);
    }
  };

  const getCitySuggestionLabel = (option) =>
    typeof option === "string" ? option : option?.name || "";

  const getCitySuggestionCoordinates = (option) => {
    if (!option || typeof option === "string") {
      return null;
    }

    const lat = Number(option.lat);
    const lng = Number(option.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    return { lat, lng };
  };

  const selectCitySuggestion = (option) => {
    setCity(getCitySuggestionLabel(option));
    setSelectedCityCoordinates(getCitySuggestionCoordinates(option));
    setCitySuggestions([]);
  };

  const toggleInList = (value, setter) => {
    setter((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const toggleMethod = (value) => toggleInList(value, setSelectedMethods);
  const toggleSpecies = (value) => toggleInList(value, setSelectedSpecies);
  const toggleBusinessType = (value) =>
    toggleInList(value, setSelectedBusinessTypes);
  const toggleTargetSpecies = (value) =>
    toggleInList(value, setSelectedTargetSpecies);

  const methodsLabel =
    selectedMethods.length === 0
      ? t("catch_type_of_fishing", { defaultValue: "Type of Fishing" })
      : selectedMethods.map((method) => t(method)).join(", ");

  const speciesLabel =
    selectedSpecies.length === 0
      ? t("select_favorite_species")
      : selectedSpecies.map((species) => t(species)).join(", ");

  const businessTypesLabel =
    selectedBusinessTypes.length === 0
      ? t("select_business_type")
      : selectedBusinessTypes.map((item) => t(item)).join(", ");

  const targetSpeciesLabel =
    selectedTargetSpecies.length === 0
      ? t("select_target_species_optional")
      : selectedTargetSpecies.map((species) => t(species)).join(", ");

  const countryValidationError = validateCountry(country, t, {
    required: false,
  });
  const cityValidationError = validateCity(city, t, {
    required: false,
  });
  const hasLocationValidationErrors = Boolean(
    countryValidationError || cityValidationError
  );

  const handleRegister = async (e) => {
    e?.preventDefault?.();

    if (registerRequestInFlightRef.current) {
      return;
    }

    const stopRegisterSubmission = () => {
      registerRequestInFlightRef.current = false;
      setIsSubmitting(false);
    };

    registerRequestInFlightRef.current = true;
    setIsSubmitting(true);

    const consentError = validateRequiredConsents({
      acceptTermsPrivacy,
      acceptProfileVisibility,
      consentTexts,
    });
    if (consentError) {
      stopRegisterSubmission();
      alert(consentError);
      return;
    }

    const step3Error = validateStep3({
      country,
      city,
      accountType,
      t,
    });
    if (step3Error) {
      stopRegisterSubmission();
      alert(step3Error);
      return;
    }

    const formData = new FormData();
    formData.append("language", language);
    formData.append("username", username);
    formData.append("email", email);
    formData.append("password", password);
    formData.append("bio", bio);

    formData.append("country", country);
    formData.append("city", city);

    if (selectedCityCoordinates) {
      formData.append(
        "locationLatitude",
        selectedCityCoordinates.lat.toString()
      );
      formData.append(
        "locationLongitude",
        selectedCityCoordinates.lng.toString()
      );
    }

    formData.append("accountType", accountType);

    formData.append("experience", accountType === "personal" ? experience : "");
    formData.append(
      "fishingStyle",
      accountType === "personal" ? fishingStyle : ""
    );
    formData.append(
      "waterType",
      accountType === "personal" ? waterType : ""
    );
    formData.append("rangeType", accountType === "personal" ? rangeType : "");
    formData.append(
      "methods",
      accountType === "personal" ? selectedMethods.join(",") : ""
    );
    formData.append(
      "favoriteSpecies",
      accountType === "personal" ? selectedSpecies.join(",") : ""
    );

    formData.append(
      "businessTypes",
      accountType === "business" ? selectedBusinessTypes.join(",") : ""
    );
    formData.append(
      "otherBusinessDescription",
      accountType === "business" ? otherBusinessDescription : ""
    );
    formData.append(
      "businessArea",
      accountType === "business" ? businessArea : ""
    );
    formData.append(
      "businessWaterType",
      accountType === "business" ? businessWaterType : ""
    );
    formData.append(
      "targetSpecies",
      accountType === "business" ? selectedTargetSpecies.join(",") : ""
    );
    formData.append(
      "contactEmail",
      accountType === "business" ? contactEmail : ""
    );

    formData.append("instagram", instagram);
    formData.append("youtube", youtube);
    formData.append("facebook", facebook);
    formData.append("website", website);
    formData.append("acceptTermsPrivacy", String(acceptTermsPrivacy));
    formData.append(
      "acceptProfileVisibility",
      String(acceptProfileVisibility)
    );

    try {
      const res = await fetch(`${API_BASE}/auth/register`, withApiCredentials({
        method: "POST",
        body: formData,
      }));

      if (!res.ok) {
        let error = null;
        try {
          error = await res.json();
        } catch {
          error = null;
        }

        if (error?.detail === "username_already_taken") {
          stopRegisterSubmission();
          alert(t("username_already_taken"));
        } else if (error?.detail === "email_already_registered") {
          stopRegisterSubmission();
          alert(t("email_already_registered"));
        } else if (isDeletedAccountAuthError(error?.detail)) {
          stopRegisterSubmission();
          alert(getDeletedAccountAuthMessage(t));
        } else if (error?.detail === "country_required") {
          stopRegisterSubmission();
          alert(validateCountry("", t));
        } else if (error?.detail === "city_required") {
          stopRegisterSubmission();
          alert(validateCity("", t));
        } else if (error?.detail === "country_invalid_chars") {
          stopRegisterSubmission();
          alert(validateCountry(country, t, { required: false }));
        } else if (error?.detail === "city_invalid_chars") {
          stopRegisterSubmission();
          alert(validateCity(city, t, { required: false }));
        } else {
          stopRegisterSubmission();
          alert(getRegisterErrorMessage(error) || t("register_failed"));
        }

        return;
      }

      const data = await res.json();
      if (data?.verificationRequired) {
        stopRegisterSubmission();
        setVerificationRequiredState({
          email: data.email || email,
          message:
            data.message ||
            t("register_verification_required_message", {
              defaultValue: "Please verify your email before logging in.",
            }),
          profileStatus: data.profileStatus || "",
        });
        return;
      }

      if (typeof data?.csrfToken === "string" && data.csrfToken.trim()) {
        setCsrfToken(data.csrfToken);
      }

      if (data?.user) {
        stopRegisterSubmission();
        setUser(data.user);
        navigate("/");
        return;
      }

      stopRegisterSubmission();
      alert(t("register_failed"));
    } catch (err) {
      stopRegisterSubmission();
      console.error("Register error:", err);
      alert(t("server_error"));
    }
  };

  const onChangeAccountType = (value) => {
    setAccountType(value);
    setOpenHelpField("");
    setShowMethodsDropdown(false);
    setShowSpeciesDropdown(false);
    setShowTargetSpeciesDropdown(false);

    setExperience("");
    setFishingStyle("");
    setWaterType("");
    setRangeType("");
    setSelectedMethods([]);
    setSelectedSpecies([]);

    setSelectedBusinessTypes([]);
    setShowBusinessTypeDropdown(false);
    setBusinessArea("");
    setBusinessWaterType("");
    setSelectedTargetSpecies([]);
    setContactEmail("");
    setOtherBusinessDescription("");
  };

  const handleLanguageChange = (lang) => {
    const nextLanguage = availableLanguages.some((item) => item.code === lang)
      ? lang
      : "en";
    setLanguage(nextLanguage);
    i18n.changeLanguage(nextLanguage);
  };

  const goToLogin = () => {
    navigate("/login");
  };

  const hasOptionalProfileDetails = Boolean(
    bio.trim() ||
      (accountType === "personal" &&
        (experience ||
          fishingStyle ||
          waterType ||
          rangeType ||
          selectedMethods.length > 0 ||
          selectedSpecies.length > 0)) ||
      instagram.trim() ||
      youtube.trim() ||
      facebook.trim() ||
      website.trim()
  );

  return {
    t,
    consentTexts,
    availableLanguages,
    fishingMethods: FISHING_METHODS,
    speciesOptions: SPECIES_OPTIONS,
    businessTypes: BUSINESS_TYPES,
    step,
    setStep,
    verificationRequiredState,
    isStep1Checking,
    isSubmitting,
    hasOptionalProfileDetails,
    language,
    username,
    setUsername,
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    bio,
    setBio,
    acceptTermsPrivacy,
    setAcceptTermsPrivacy,
    acceptProfileVisibility,
    setAcceptProfileVisibility,
    country,
    setCountry,
    countryValidationError,
    countrySuggestions,
    citySuggestions,
    city,
    setCity,
    cityValidationError,
    hasLocationValidationErrors,
    selectedCityCoordinates,
    accountType,
    experience,
    setExperience,
    fishingStyle,
    setFishingStyle,
    waterType,
    setWaterType,
    rangeType,
    setRangeType,
    selectedMethods,
    selectedSpecies,
    selectedBusinessTypes,
    showBusinessTypeDropdown,
    setShowBusinessTypeDropdown,
    otherBusinessDescription,
    setOtherBusinessDescription,
    businessArea,
    setBusinessArea,
    businessWaterType,
    setBusinessWaterType,
    selectedTargetSpecies,
    instagram,
    setInstagram,
    youtube,
    setYoutube,
    facebook,
    setFacebook,
    website,
    setWebsite,
    contactEmail,
    setContactEmail,
    showMethodsDropdown,
    setShowMethodsDropdown,
    showSpeciesDropdown,
    setShowSpeciesDropdown,
    showTargetSpeciesDropdown,
    setShowTargetSpeciesDropdown,
    openHelpField,
    setOpenHelpField,
    methodsLabel,
    speciesLabel,
    businessTypesLabel,
    targetSpeciesLabel,
    handleStep1,
    handleStep2,
    handleCountryChange,
    selectCountrySuggestion,
    handleCityChange,
    getCitySuggestionLabel,
    getCitySuggestionCoordinates,
    selectCitySuggestion,
    toggleMethod,
    toggleSpecies,
    toggleBusinessType,
    toggleTargetSpecies,
    handleRegister,
    onChangeAccountType,
    handleLanguageChange,
    goToLogin,
  };
}
